<template>
  <div class="ncrm-widget-card ncrm-trend-card">
    <div class="ncrm-widget-card__header">
      <div class="ncrm-widget-card__header-left">
        <v-icon size="15" class="mr-1" style="opacity:0.65">mdi-chart-bar</v-icon>
        <span class="ncrm-widget-card__title">{{ $t("ncrm.board.widgets.weeklyTrend.title") }}</span>
      </div>
      <div class="ncrm-trend-legend">
        <span class="ncrm-trend-legend__item">
          <span class="ncrm-trend-legend__dot ncrm-trend-legend__dot--done"></span>
          {{ $t("ncrm.board.widgets.weeklyTrend.done") }}
        </span>
        <span class="ncrm-trend-legend__item">
          <span class="ncrm-trend-legend__dot ncrm-trend-legend__dot--overdue"></span>
          {{ $t("ncrm.board.widgets.weeklyTrend.overdue") }}
        </span>
      </div>
    </div>

    <div class="ncrm-trend-body">
      <v-skeleton-loader v-if="loading" type="image" height="140" />
      <div v-else-if="!weeklyTrendChart" class="ncrm-widget-card__empty">
        <v-icon size="32" style="opacity:0.25">mdi-chart-bar</v-icon>
        <span>{{ $t("ncrm.board.widgets.weeklyTrend.noData") }}</span>
      </div>
      <svg
        v-else
        class="ncrm-trend-svg"
        :viewBox="weeklyTrendChart.viewBox"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <line
          v-for="gl in weeklyTrendChart.yGridLines"
          :key="'gl' + gl.y"
          x1="0" :y1="gl.y"
          :x2="weeklyTrendChart.chartW" :y2="gl.y"
          stroke="rgba(44,62,80,0.07)" stroke-width="1"
        />

        <g v-for="b in weeklyTrendChart.bars" :key="b.day">
          <rect v-if="b.isToday"
            :x="b.cx - weeklyTrendChart.groupW / 2 + 2"
            :y="0"
            :width="weeklyTrendChart.groupW - 4"
            :height="weeklyTrendChart.chartH"
            fill="rgba(36,200,198,0.05)"
            rx="4"
          />
          <rect
            :x="b.doneX" :y="b.doneH > 0 ? b.doneY : weeklyTrendChart.chartH - 2"
            :width="b.barW" :height="b.doneH > 0 ? b.doneH : 2"
            rx="3" fill="#2c3e50" opacity="0.82"
          />
          <rect
            :x="b.overdueX" :y="b.overdueH > 0 ? b.overdueY : weeklyTrendChart.chartH - 2"
            :width="b.barW" :height="b.overdueH > 0 ? b.overdueH : 2"
            rx="3" fill="#dc3545" opacity="0.72"
          />
          <text
            :x="b.labelX" :y="b.labelY"
            text-anchor="middle"
            font-size="12"
            :font-weight="b.isToday ? '700' : '500'"
            :fill="b.isToday ? '#2c3e50' : '#8a98a8'"
          >{{ b.label }}</text>
          <circle v-if="b.isToday" :cx="b.labelX" :cy="b.labelY + 7" r="2.5" fill="#24c8c6" />
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
const padDatePart = (value) => String(value).padStart(2, "0");
const localDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

export default {
  name: "NcrmBoardWeeklyTrend",
  props: {
    days: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    weeklyTrendChart() {
      const days = this.days;
      if (!days.length) return null;

      const CHART_W = 640, CHART_H = 110;
      const BAR_W = 22, HALF_GAP = 3;
      const groupW = CHART_W / days.length;
      const allValues = days.flatMap((d) => [d.done, d.overdue]);
      const maxVal = Math.max(...allValues, 1);
      const step = maxVal <= 5 ? 5 : maxVal <= 10 ? 10 : maxVal <= 30 ? 10 : Math.ceil(maxVal / 5) * 5;
      const niceMax = Math.ceil(maxVal / step) * step;
      const scale = (v) => (v <= 0 ? 0 : Math.max(3, (v / niceMax) * CHART_H));
      const todayStr = localDateKey(new Date());

      return {
        viewBox: `0 0 ${CHART_W} ${CHART_H + 28}`,
        chartW: CHART_W,
        chartH: CHART_H,
        groupW,
        yGridLines: [0, Math.round(niceMax / 2), niceMax].map((v) => ({
          y: CHART_H - (v / niceMax) * CHART_H,
          label: v,
        })),
        bars: days.map((d, i) => {
          const cx = i * groupW + groupW / 2;
          const doneH = scale(d.done);
          const overdueH = scale(d.overdue);
          return {
            day: d.day,
            label: this.formatWeeklyTrendDayLabel(d.day),
            cx,
            groupW,
            doneX: cx - HALF_GAP - BAR_W,
            overdueX: cx + HALF_GAP,
            doneY: CHART_H - doneH,
            overdueY: CHART_H - overdueH,
            doneH,
            overdueH,
            barW: BAR_W,
            isToday: d.day === todayStr,
            labelX: cx,
            labelY: CHART_H + 18,
          };
        }),
      };
    },
  },
  methods: {
    resolveCurrentLocale() {
      const locale = this.$i18n?.locale?.value || this.$i18n?.locale || "ro";
      return String(locale || "ro").trim() || "ro";
    },
    formatWeeklyTrendDayLabel(day) {
      const date = new Date(`${day}T12:00:00`);
      if (Number.isNaN(date.getTime())) return "";
      const format = (locale) =>
        new Intl.DateTimeFormat(locale, { weekday: "short" })
          .format(date)
          .replace(/\.$/, "");
      try {
        return format(this.resolveCurrentLocale());
      } catch (_) {
        return format("ro");
      }
    },
  },
};
</script>

<style scoped>
.ncrm-widget-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  background: var(--app-content-background);
  box-shadow: var(--app-shadow-sm);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ncrm-widget-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

.ncrm-widget-card__header-left {
  display: flex;
  align-items: center;
}

.ncrm-widget-card__title {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--app-text-strong);
}

.ncrm-widget-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 160px;
  color: var(--app-text-default);
  opacity: 0.6;
  font-size: 0.88rem;
  text-align: center;
}

.ncrm-trend-body {
  padding: 12px 20px 16px;
  min-height: 160px;
  display: flex;
  align-items: center;
}

.ncrm-trend-svg {
  width: 100%;
  height: 150px;
  display: block;
  overflow: visible;
}

.ncrm-trend-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

.ncrm-trend-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--app-text-default);
  opacity: 0.8;
}

.ncrm-trend-legend__dot {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  flex-shrink: 0;
}

.ncrm-trend-legend__dot--done { background: #2c3e50; }
.ncrm-trend-legend__dot--overdue { background: #dc3545; }
</style>
